import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  InputAdornment,
  Fade,
} from "@mui/material";
import React, { useState, useEffect, useCallback, useRef } from "react";

import { useHandleClients } from "0.Mine/1.Hooks/CRM/client";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";

// Remove ClientSearchResult interface and use Record<number, string> instead
interface ClientQueryProps {
  onClientSelect?: (clientId: number, clientName: string) => void;
  placeholder?: string;
  label?: string;
}

// Custom debounce function implementation
const useDebounce = <T extends (...args: any[]) => any>(callback: T, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
};

const ClientQuery: React.FC<ClientQueryProps> = ({
  onClientSelect,
  placeholder = "Ricerca cliente...",
  label = "Ricerca cliente",
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [results, setResults] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const { searchClient } = useHandleClients();
  const { session } = useBusinessContext();

  // Search function
  const performSearch = useCallback(
    async (term: string) => {
      if (term.length < 2) {
        setResults({});
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await searchClient(session.selectedBusiness.id, term);

        if (response && response.simplifiedRecords) {
          setResults(response.simplifiedRecords);
        } else {
          setResults({});
          if (!response) {
            setError("No results found");
          }
        }
      } catch (err) {
        console.error("Search error:", err);
        setError("An error occurred while searching. Please try again.");
        setResults({});
      } finally {
        setIsLoading(false);
      }
    },
    [session.selectedBusiness.id, searchClient]
  );

  // Create debounced search using our custom hook
  const debouncedSearch = useDebounce(performSearch, 500);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setIsSearching(value.length > 0);

    if (value.length === 0) {
      setResults({});
      setError(null);
      setIsSearching(false);
    } else {
      debouncedSearch(value);
    }
  };

  // Handle client selection
  const handleClientSelect = (clientId: number, clientName: string) => {
    if (onClientSelect) {
      onClientSelect(clientId, clientName);
    }
    setSearchTerm(clientName);
    setResults({});
    setIsSearching(false);
  };

  return (
    <Box sx={{ position: "relative", width: "100%", marginTop: "30px" }}>
      <TextField
        fullWidth
        label={label}
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={placeholder}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading && <CircularProgress size={20} />}
            </InputAdornment>
          ),
        }}
      />

      {isSearching && (
        <Fade in={true}>
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              width: "100%",
              maxHeight: "300px",
              overflowY: "auto",
              mt: 1,
              zIndex: 1300,
            }}
          >
            {error ? (
              <Box p={2}>
                <Typography color="error">{error}</Typography>
              </Box>
            ) : Object.keys(results).length > 0 ? (
              <List dense>
                {Object.entries(results).map(([idStr, name]) => {
                  const id = parseInt(idStr);
                  return (
                    <ListItem
                      button
                      key={id}
                      onClick={() => handleClientSelect(id, name)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={name} secondary={`ID: ${id}`} />
                    </ListItem>
                  );
                })}
              </List>
            ) : !isLoading && searchTerm.length >= 2 ? (
              <Box p={2}>
                <Typography color="text.secondary">Nessun cliente trovato</Typography>
              </Box>
            ) : searchTerm.length < 2 ? (
              <Box p={2}>
                <Typography color="text.secondary">
                  Scrivi almeno 2 lettere per iniziare la ricerca...
                </Typography>
              </Box>
            ) : null}
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default ClientQuery;
